<template>
    <div
        class="o-d-guide d-guide"
        :class="{'d-guide--white': isWhiteBackground}"
    >
        <Aline />
        <component
            :is="currentUnit"
            :item="item"
            :start-point="startPoint"
            :finish-point="finishPoint"
            :mode="mode"
            @onChangePoint="changePoint"
        />
    </div>
</template>

<script>
import {
    mapState,
    mapGetters
} from 'vuex';

import Aline from '../../atoms/a-line/a-line';
import ODGuideSearch from '../../unique-organisms/o-d-guide-search/o-d-guide-search';
import ODGuidePathPanel from '../../unique-organisms/o-d-guide-path-panel/o-d-guide-path-panel';

import {
    GUIDE_NEW_POINT,
    START_GUIDE_OVERVIEW,
    GUIDE_CLOSE_SEARCHBAR
} from '../../../utils/constants/bus-events-constants';
import { MAP_SHOW_PATH } from "../../../shared/components/map-viewer/constants/map-commands";
import {
    UI_MAP_READY,
    UI_STEP_BY_STEP_DIRECTIONS
} from "../../../shared/components/map-viewer/constants/map-events";

export default {
    name: 'ODGuide',
    components: {
        Aline,
        'search': ODGuideSearch,
        'navigation': ODGuidePathPanel
    },
    data() {
        return {
            mode: null,
            to: this.$route.query.to || null,
            from: this.$route.query.from || null,
            currentUnit: 'navigation',
        }
    },
    computed: {
        ...mapState({
            isAppLoaded: state => state.appLoaded,
            isMapLoading: state => state.mapLoading
        }),
        ...mapGetters([
            't',
            'getObject',
            'getMapConfigProp',
            'getDenormalisedObjectById',
            'getDenormalisedObjectsById',
        ]),
        item() {
            return this.mode === 'start' ? this.startPoint : this.finishPoint;
        },
        startPoint: {
            get() {
                const isQrRedirect = this.$route.params.qr || false;
                const isClosest = this.$route.params.closest || false;
                // eslint-disable-next-line camelcase
                const closestEntranceId = this.finishPoint?.meta?.closest_entrance || this.getMapConfigProp('guide.default_entrance', );

                if(!closestEntranceId)
                    return
                // for outdoor destinations use indoor entrance unit and wise versa
                const closestOutdoorEntrance = this.getDenormalisedObjectsById(closestEntranceId).find( o => o.floor.meta.is_outdoor)

                //TODO: re-architect data flow (this.$route.query.from > startPoint > startPointObject > showPath)
                if (isQrRedirect) {
                    return this.$route.query.from
                        ? this.getDenormalisedObjectById(parseInt(this.from))
                        : isClosest ? {
                            name: this.t(closestOutdoorEntrance, 'name'),
                            id: closestOutdoorEntrance.unit.name,
                            object_id: closestOutdoorEntrance.id
                        } : {}
                } else {
                    return this.$route.query.from
                        ? this.getDenormalisedObjectById(parseInt(this.from))
                        : {
                            name: this.t(closestOutdoorEntrance, 'name'),
                            id: closestOutdoorEntrance.unit.name,
                            object_id: closestOutdoorEntrance.id
                        }
                }
            },
            set(data) {
                this.from = data;
            }
        },
        startPointObject(){
            return this.startPoint && this.getDenormalisedObjectById(this.startPoint.object_id || this.startPoint.id)
        },
        finishPoint: {
            get() {
                return this.to ? this.getDenormalisedObjectById(parseInt(this.to, 10)) : {};
            },
            set(data) {
                this.to = data;
            }
        },
        isWhiteBackground() {
            return this.currentUnit === 'search'
        }
    },
    mounted() {
        this.initEventsListeners();
        this.isAppLoaded && !this.isMapLoading && this.buildPath();
        this.checkQrParams();
    },
    beforeDestroy() {
        this.deactivateEventsListeners();
    },
    watch: {
        '$route.query.to'(value) {
            this.to = value;
            this.buildPath();
        },
        '$route.query.from'(value) {
            this.from = value;
        }
    },
    methods: {
        initEventsListeners() {
            this.$bus.$on(UI_MAP_READY, this.buildPath);
            this.$bus.$on(GUIDE_NEW_POINT, this.setNewPoint);
            this.$bus.$on(START_GUIDE_OVERVIEW, this.buildPath);
            this.$bus.$on(UI_STEP_BY_STEP_DIRECTIONS, this.setPath);
            this.$bus.$on(GUIDE_CLOSE_SEARCHBAR, this.closeSearchbar);
        },
        deactivateEventsListeners() {
            this.$bus.$off(UI_MAP_READY, this.buildPath);
            this.$bus.$off(GUIDE_NEW_POINT, this.setNewPoint);
            this.$bus.$off(START_GUIDE_OVERVIEW, this.buildPath);
            this.$bus.$off(UI_STEP_BY_STEP_DIRECTIONS, this.setPath);
            this.$bus.$off(GUIDE_CLOSE_SEARCHBAR, this.closeSearchbar);
        },
        closeSearchbar() {
            this.currentUnit = 'navigation';
            this.buildPath();
        },
        setNewPoint(data) {
            const params = {
                query: {}
            };
            if (this.mode === 'start') {
                if (parseInt(this.$route.query.from, 10) === data.id) {
                    this.currentUnit = 'navigation';
                    this.buildPath();
                    return;
                }
                this.startPoint = data.id;
                params.query.from = data.id;
                params.query.to = this.to;
            } else {
                if (parseInt(this.$route.query.to, 10) === data.id) {
                    this.currentUnit = 'navigation';
                    this.buildPath();
                    return;
                }
                if (this.$route.query.from) {
                    params.query.from = this.from;
                    params.query.to = data.id;
                } else {
                    params.query.to = data.id;
                }
                this.finishPoint = data.id;
            }
            this.currentUnit = 'navigation';
            this.$router.replace(params);
            this.buildPath();
        },
        changePoint(data) {
            this.mode = data;
            this.currentUnit = 'search';
        },
        resetActiveStep() {
            this.$store.commit('setActiveStepIndex', null);
            this.$store.commit('setActiveStep', null);
        },
        buildPath() {
            if(
                !_.has(this.finishPoint, 'id') ||
                (this.$route.query.qr && this.$route.query.to) ||
                !this.startPointObject
            ) return;

            this.resetActiveStep();
            const params = {
                from: {},
                to: {
                    id: this.finishPoint.id
                },
                mode: this.getMapConfigProp('guide.mode', 'easy'),
            };
            if(_.has(this.startPointObject, 'meta.settings.guide_orientation') )
                params.initial_orientation = this.startPointObject.meta.settings.guide_orientation
            params.from = this.$route.query.from && this.startPointObject
                ? {
                    id: this.startPointObject.id,
                    unit: this.startPointObject.unit.name
                }
                : {
                    id: this.startPoint.object_id,
                    unit: this.startPoint.id
                };

            // eslint-disable-next-line camelcase
            if(this.finishPoint?.meta?.prioritise_outdoor)
                params.use_outside = 'prefer';

            params.portal_rules = this.getMapConfigProp('guide.portal_rules', {});

            if(!this.$route.params.qr && !this.$route.query.from )
                params.show_first_step_portal_message = true

            setTimeout( () => this.$bus.$emit(MAP_SHOW_PATH, params), 500);
        },
        setPath(data) {
            this.$store.commit('setActivePathToState', data);
        },
        checkQrParams() {
            if (this.$route.query.qr && this.$route.query.from && !this.$route.query.to) {
                this.currentUnit = 'search';
                this.mode = 'finish';
            } else if (this.$route.query.qr && this.$route.query.to) {
                this.currentUnit = 'search';
                this.mode = 'start';
            }
        }
    }
}
</script>

<style lang="scss">
@import "o-d-guide";
</style>
